/**
 * PackageDetail — EntityDetailShell-based Package detail page (11E.4 §C).
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { PageContainer } from '@/components/layout';
import { EntityDetailShell } from '@/components/entity';
import { usePackage } from '@features/catalog/api/packages';
import { useSubjects } from '@features/catalog/api/subjects';
import { formatPaise } from '@features/catalog/config/ui';
import { buildPackageDetailConfig } from '@features/packages/config/packagesEntityConfig';
import { deriveCycleLabel } from '@features/packages/config/nameDerivation';

export const PackageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: pkg, isLoading, isError } = usePackage(id ?? '');
  const { data: subjectsData } = useSubjects({ status: 'active' });
  const subjectNameById = new Map((subjectsData?.items ?? []).map((s) => [s.id, s.name]));

  if (isLoading) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg"><p style={{ color: '#64748b' }}>Loading package…</p></Card>
      </PageContainer>
    );
  }

  if (isError || !pkg) {
    return (
      <PageContainer title="Package Not Found">
        <Card padding="lg"><p>The package you are looking for does not exist.</p></Card>
      </PageContainer>
    );
  }

  const subjectNames = pkg.subject_ids.map((sid) => subjectNameById.get(sid) ?? sid);

  const overviewContent = (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 24,
          marginBottom: 20,
        }}
      >
        <Field label="Type" value={pkg.package_type === 'subscription' ? (pkg.billing_cycle ?? 'subscription') : deriveCycleLabel(pkg)} />
        <Field label="Price" value={formatPaise(pkg.price)} />
        <Field label="Registration fee" value={formatPaise(pkg.registration_fee)} />
        <Field label="Audience" value={pkg.audience_label} />
        <Field label="Make-up sessions" value={pkg.makeup_sessions_allowed ? 'Allowed' : 'Not allowed'} />
        <Field label="Published online" value={pkg.published_online ? 'Yes' : 'No'} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Subjects</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {subjectNames.map((n) => <Badge key={n} variant="primary">{n}</Badge>)}
        </div>
      </div>
      {pkg.add_ons.length > 0 && (
        <div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Add-ons</div>
          {pkg.add_ons.map((a) => (
            <div key={a.name} style={{ fontSize: 13, marginBottom: 2 }}>
              {a.name} — {formatPaise(a.price)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const detailConfig = buildPackageDetailConfig({
    subjectNames,
    renderOverviewTab: () => overviewContent,
  });

  return (
    <EntityDetailShell
      entity={pkg}
      config={detailConfig}
      onEdit={() => navigate(`/packages/${pkg.id}/edit`)}
      canEdit
    />
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
  </div>
);

